import { useCallback } from "react";

export function useHarvestEntryColumnWidths({ gridWidth, showWeight }) {
  const computeWidths = useCallback(() => {
    const SELECT_W = 44;

    const keys = showWeight
      ? ["bayRow", "status", "date", "yield", "weight", "tubWeight", "ploughedReasons", "ploughedNote"]
      : ["bayRow", "status", "date", "yield", "tubWeight", "ploughedReasons", "ploughedNote"];

    // Minimums sized so each column header is fully visible (22 chars max at ~7.5px/char + 16px padding).
    const min = {
      bayRow: 170,
      status: 95,
      date: 185,          // "Harvest / Discard Date" (22 chars, ~181px)
      yield: 100,         // "No. of Tubs" (11 chars, ~98px)
      weight: 90,
      tubWeight: 95,      // "Tub Weight" (10 chars, ~91px)
      ploughedReasons: 125, // "Discard Reason" (14 chars, ~121px)
      ploughedNote: 110,  // "Discard Note" (12 chars, ~106px)
    };

    // Low weights for compact-content columns → stay near their minimum.
    // High weights for filter/input columns → absorb the extra space.
    const wt = showWeight
      ? { bayRow: 0.22, status: 0.13, date: 0.10, yield: 0.07, weight: 0.06, tubWeight: 0.07, ploughedReasons: 0.18, ploughedNote: 0.17 }
      : { bayRow: 0.25, status: 0.15, date: 0.12, yield: 0.08, tubWeight: 0.08, ploughedReasons: 0.17, ploughedNote: 0.15 };

    const available = Math.max(0, (gridWidth || 0) - SELECT_W);
    if (available <= 0) {
      const fallback = {};
      keys.forEach((k) => (fallback[k] = min[k]));
      return fallback;
    }

    const width = {};
    keys.forEach((k) => {
      width[k] = Math.round(available * (wt[k] || 0));
    });

    // Enforce minimums — guarantees every header is visible.
    keys.forEach((k) => {
      width[k] = Math.max(min[k], width[k]);
    });

    const sum = () => keys.reduce((s, k) => s + (width[k] || 0), 0);
    let diff = available - sum();

    const shrinkableKeys = () => keys.filter((k) => width[k] > min[k]);

    while (diff < 0) {
      const canShrink = shrinkableKeys();
      if (!canShrink.length) break;

      const totalShrinkRoom = canShrink.reduce(
        (s, k) => s + (width[k] - min[k]),
        0,
      );
      if (totalShrinkRoom <= 0) break;

      for (const k of canShrink) {
        if (diff === 0) break;
        const room = width[k] - min[k];
        if (room <= 0) continue;

        const portion = room / totalShrinkRoom;
        const dec = Math.min(room, Math.ceil(Math.abs(diff) * portion));
        width[k] -= dec;
        diff += dec;
      }
    }

    if (diff > 0) {
      const order = showWeight
        ? ["bayRow", "status", "ploughedReasons", "ploughedNote", "date", "yield", "tubWeight", "weight"]
        : ["bayRow", "status", "ploughedReasons", "ploughedNote", "date", "yield", "tubWeight"];

      let i = 0;
      while (diff > 0) {
        width[order[i % order.length]] += 1;
        diff -= 1;
        i += 1;
      }
    }

    return width;
  }, [gridWidth, showWeight]);

  return { computeWidths };
}
